import React, { useState, useEffect, useRef } from 'react';
import { Mail, Shield, AlertCircle, CheckCircle2, ArrowLeft, Send } from 'lucide-react';
import type { ContactInfo } from '../types';
import { signUpWithOTP, verifyOTP } from '../utils/auth';
import { supabase } from '../lib/supabase';

const getErrorMessage = (err: unknown): string => {
  if (typeof err === 'string') {
    return err;
  }

  if (err && typeof err === 'object' && 'message' in err) {
    const { message } = err as { message?: unknown };
    if (typeof message === 'string') {
      return message;
    }
    return message != null ? String(message) : '';
  }

  return '';
};

interface OTPVerificationProps {
  onVerified: () => void;
  contactInfo?: ContactInfo & { password?: string; accountType: string };
  onBack?: () => void;
  error?: string | null;
}

export function OTPVerification({ onVerified, contactInfo, onBack, error: propError }: OTPVerificationProps) {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  useEffect(() => {
    // Don't automatically send OTP - user must click the button to send verification code
  }, [contactInfo]);

  const sendVerificationOTP = async () => {
    if (!contactInfo) return;
    
    setIsSendingEmail(true);
    setError(null);
    
    try {
      // Send OTP for verification since account already exists
      const result = await signUpWithOTP(contactInfo.email, {
        data: {
          name: contactInfo.name,
          phone: contactInfo.phone,
          accountType: contactInfo.accountType
        }
      });
      
      if (result.error) {
        const errorMessage = getErrorMessage(result.error);

        // Handle specific error cases
        if (errorMessage.includes('already exists')) {
          // This is actually expected since account exists, just send OTP
          setEmailSent(true);
          setRetryCount(0);
          return;
        }

        throw new Error(errorMessage || 'Failed to send verification email');
      }
      
      setEmailSent(true);
      setRetryCount(0);
    } catch (err) {
      const errorMessage = getErrorMessage(err) || 'Failed to send verification email';
      setError(errorMessage);
      setRetryCount(prev => prev + 1);
      
      if (retryCount >= 2) {
        setError("We're having trouble sending the verification code. Please check your email address or try again later. If the problem persists, contact support@entek.ai");
      }
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactInfo) return;

    setIsLoading(true);
    setError(null);

    try {
      const enteredCode = otp.join('');
      
      const { data, error: verifyError } = await verifyOTP(contactInfo.email, enteredCode);

      if (verifyError) {
        const verifyMessage = getErrorMessage(verifyError);

        // Check if the error indicates user already exists
        if (verifyMessage.includes('already exists') || verifyMessage.includes('duplicate key')) {
          throw new Error('An account with this email already exists. Please sign in instead or use a different email address.');
        }
        throw new Error(verifyMessage || 'Verification failed. Please try again.');
      }

      const typedData = (data ?? {}) as { user?: unknown };
      if (!typedData.user) {
        throw new Error('Verification failed. Please try again.');
      }

      // Since the account is already created and profile exists, we just need to ensure the user is logged in
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        // If no session, try to refresh
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !refreshData.session) {
          throw new Error('Failed to establish session. Please try logging in again.');
        }
      }

      // User is now logged in, proceed to next step
      onVerified();
    } catch (err) {
      let errorMessage = getErrorMessage(err) || 'Verification failed';
      
      // Enhance error messages for common scenarios
      if (errorMessage.includes('expired')) {
        errorMessage = 'The verification code has expired. Please request a new code.';
      } else if (errorMessage.includes('invalid')) {
        errorMessage = 'Invalid verification code. Please check the code and try again.';
      } else if (errorMessage.includes('already exists')) {
        errorMessage = 'An account with this email already exists. Please sign in instead or use a different email address.';
      }
      
      setError(errorMessage);
      
      // Clear OTP fields on error
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && index > 0 && !otp[index]) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      inputRefs.current[5]?.focus();
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-200">
        {onBack && (
          <div className="flex justify-start mb-6">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </button>
          </div>
        )}

        <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
          <Mail className="h-8 w-8 text-blue-600" />
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Verify Your Email
        </h2>
        
        {!emailSent ? (
          <div className="space-y-4">
            <p className="text-gray-600 mb-6">
              We need to verify your email address to complete your account setup.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center text-blue-800">
                <Shield className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-sm font-medium">
                  Click the button below to send a verification code to <strong>{contactInfo?.email}</strong>
                </span>
              </div>
            </div>

            <button
              onClick={sendVerificationOTP}
              disabled={isSendingEmail}
              className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-lg shadow-lg flex items-center justify-center"
            >
              {isSendingEmail ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Sending Code...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Send Verification Code
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center text-green-800">
                <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-sm font-medium">
                  Verification code sent to <strong>{contactInfo?.email}</strong>
                </span>
              </div>
            </div>

            <p className="text-gray-600 mb-6">
              Please check your email and enter the 6-digit verification code below.
              <br />
              <span className="text-sm text-gray-500 mt-2 block">
                Don't see it? Check your spam folder or click "Send New Code" below.
              </span>
            </p>

            {(propError || error) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center text-red-700">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                  {propError || error}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex justify-center gap-3" onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    maxLength={1}
                    className="w-14 h-14 text-center text-2xl font-bold rounded-lg border-2 border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 shadow-sm"
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    disabled={isLoading}
                    autoComplete="one-time-code"
                  />
                ))}
              </div>

              <button
                type="submit"
                disabled={isLoading || otp.join('').length !== 6}
                className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>

              <button
                type="button"
                onClick={sendVerificationOTP}
                className="w-full text-blue-600 hover:text-blue-700 transition-colors py-2 px-4 border border-blue-200 rounded-lg hover:bg-blue-50 font-medium"
                disabled={isSendingEmail}
              >
                {isSendingEmail ? 'Sending New Code...' : 'Send New Code'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
